import { useTheme } from "emotion-theming";
import useMediaQuery from "../useMediaQuery";
import { useMemo } from "react";
import { NectarTheme } from "../../../typings/theme";

interface BreakpointsMediaQueries {
  xs: boolean;
  sm: boolean;
  md: boolean;
  lg: boolean;
  xl: boolean;
}

const useScreenBreakpoint = () => {
  const { breakpoints } = useTheme<NectarTheme>();

  const breakpointsMediaQueries: BreakpointsMediaQueries = {
    xs: useMediaQuery(breakpoints.xs, true),
    sm: useMediaQuery(breakpoints.sm, true),
    md: useMediaQuery(breakpoints.md, true),
    lg: useMediaQuery(breakpoints.lg, true),
    xl: useMediaQuery(breakpoints.xl, true),
  };

  const arrayOfBreakpoints = Object.keys(breakpoints) as Array<keyof typeof breakpoints>;

  const getScreenBreakpointsByHierarchy = (breakpoint: keyof typeof breakpoints) => {
    const breakpointIndex = arrayOfBreakpoints.indexOf(breakpoint);
    const screenIsLargerThanBreakpoint = breakpointsMediaQueries[breakpoint];
    const screenIsLargerThanAnyHigherHierarchyBreakpoint = arrayOfBreakpoints
      .slice(breakpointIndex + 1)
      .some(
        (higherHierarchyBreakpoint: keyof typeof breakpoints) =>
          breakpointsMediaQueries[higherHierarchyBreakpoint]
      );
    return screenIsLargerThanBreakpoint && !screenIsLargerThanAnyHigherHierarchyBreakpoint;
  };

  const [isMobileScreen, isSmallScreen, isMediumScreen, isLargeScreen, isExtraLargeScreen] =
    arrayOfBreakpoints.map((breakpoint) => getScreenBreakpointsByHierarchy(breakpoint));

  const isBiggerScreens = isMediumScreen || isLargeScreen || isExtraLargeScreen;
  const isUltrawideScreens = isLargeScreen || isExtraLargeScreen;
  const isMedianScreens = isMediumScreen || isLargeScreen;
  const isSmallerScreens = isMobileScreen || isSmallScreen;

  const screenBreakpoint = useMemo(() => {
    if (isExtraLargeScreen) return "xl";
    if (isLargeScreen) return "lg";
    if (isSmallScreen) return "sm";
    if (isMobileScreen) return "xs";
    return "md";
  }, [isExtraLargeScreen, isLargeScreen, isSmallScreen, isMobileScreen]);

  return {
    screenBreakpoint,
    isExtraLargeScreen,
    isLargeScreen,
    isMediumScreen,
    isSmallScreen,
    isMobileScreen,
    isBiggerScreens,
    isUltrawideScreens,
    isMedianScreens,
    isSmallerScreens,
  };
};

export default useScreenBreakpoint;
